/**
 * Helpers for async multiselect filters where the UI stores **stable ids** but list POST bodies need **display strings**.
 * Pair with {@link useAsyncFilterOptionLabelMaps} and accordion `onAsyncOptionsChunk`.
 */

/** Merge one option into an id→label map (stores both exact and lowercased id keys for lookup resilience). */
export function mergeIdLabelIntoMap(map: Map<string, string>, id: string, label: string): void {
  const idTrim = String(id ?? "").trim();
  const lab = String(label ?? "").trim();
  if (!idTrim) return;
  map.set(idTrim, lab);
  map.set(idTrim.toLowerCase(), lab);
}

export function lookupIdLabelMap(map: Map<string, string>, key: string): string | undefined {
  const k = String(key ?? "").trim();
  if (!k) return undefined;
  return map.get(k) ?? map.get(k.toLowerCase());
}

/**
 * Turn selected id keys into payload keyword strings using a populated id→label map.
 * Omits entries with no label (unknown id); drops "all" / empty.
 */
export function collectPayloadStringsFromIdSelection(
  selectedRec: Record<string, boolean> | undefined,
  idToLabel: Map<string, string>
): string[] {
  return Object.entries(selectedRec ?? {})
    .filter(([, v]) => v)
    .map(([idKey]) => lookupIdLabelMap(idToLabel, idKey))
    .filter((s): s is string => s != null && String(s).trim() !== "")
    .filter((s) => {
      const lo = s.trim().toLowerCase();
      return lo !== "all" && lo !== "";
    });
}

/** Build empty maps for each logical namespace (e.g. `site`, `product`). */
export function createEmptyIdLabelMapRecord(namespaceKeys: readonly string[]): Record<string, Map<string, string>> {
  const out: Record<string, Map<string, string>> = {};
  for (const k of namespaceKeys) {
    out[k] = new Map();
  }
  return out;
}

export function stableNamespaceFingerprint(namespaceKeys: readonly string[]): string {
  return [...namespaceKeys].sort().join("\0");
}
